import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { resendEmailVerification } from '../api/auth'
import { Mail, CheckCircle, RefreshCw } from 'lucide-react'

const EmailVerificationRequiredPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState('')

  const email = searchParams.get('email') || ''

  const handleResendVerification = async () => {
    if (!email) return
    
    setIsResending(true)
    setResendError('')
    try {
      await resendEmailVerification(email)
      setResendSuccess(true)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification email. Please try again.'
      setResendError(errorMessage)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-brand-100 mb-6">
            <Mail className="h-8 w-8 text-brand-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Verify your email address
          </h1>

          <p className="text-gray-600 mb-6">
            We've sent a verification email to <strong>{email}</strong>. 
            Please check your inbox and click the verification link to continue.
          </p>

          {resendSuccess ? (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-success-600 mr-2" />
                <p className="text-success-800 text-sm">
                  Verification email sent successfully! Please check your inbox.
                </p>
              </div>
            </div>
          ) : null}

          {resendError ? (
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-6">
              <p className="text-danger-800 text-sm">
                {resendError}
              </p>
            </div>
          ) : null}

          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Didn't receive the email? Check your spam folder or click below to resend.
            </p>
            
            <button
              onClick={handleResendVerification}
              disabled={isResending || !email}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend verification email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailVerificationRequiredPage
